import React, { useState, useEffect, useRef, useMemo } from "react"
import { useQuery, useQueryClient } from "react-query"
import {
  fetchDeals,
  fetchOpenPositions,
} from "../../../services/main.service.ts"
import Paginator from "../../composites/paginator.tsx"
import WithAuth from "../../hoc/with-auth.tsx"
import { LayoutWithSidebar } from "../../layouts/layout-with-sidebar.tsx/index.tsx"
import {
  Badge,
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  Grid,
  GridItem,
  Button,
  Link as CLink,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Select,
} from "@chakra-ui/react"
import _ from "lodash"
import BlockedLoader from "../../composites/blocked-loader.tsx"
import DateRangePicker from "../../composites/date-range-picker.tsx"
import { AiOutlineSearch } from "react-icons/ai"
import { Input, InputWrapper } from "../../composites/index.tsx"
import { ReactSelect } from "../../composites/select.tsx"
import { SelectSymbols } from "../../composites/select-symbols.tsx"
import { CSVLink, CSVDownload } from "react-csv"
import { AiOutlineDownload } from "react-icons/ai"
import {
  createSharingLogicService,
  fetchCommissionLogicService,
  getTradesByIPService,
} from "../../../services/sql.service.ts"
import { FaPlus, FaDownload } from "react-icons/fa"
import { useFormik } from "formik"
import { useShowToastHook } from "../../../hooks/show-toast-hook.ts"
import { getTradeHistoryOfAllUsers } from "../../../services/crm.service.ts"
import { formatDate } from "../../../utils/formatDate.ts"

const captions = [
  "IP Addresss",
  "Deal",
  "Position",
  "Login",
  "Name",
  "Time",
  "Entry",
  "Symbol",
  "Volume",
  "Price",
  "S/L",
  "T/P",
  "Reason",
  "Profit",
  "Dealer",
  "Comment",
]

const CommissionReportPage = () => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [isGeneratingReport, setIsGeneratingReport] = useState(false)
  const [isSetForcedUpdate, setIsForcedUpdate] = useState(false)
  const [year, setyear] = useState("2023")
  const [currentPage, setCurrentpage] = useState(1)

  const [[startDate, endDate], setDateRange] = useState<
    [Date | undefined, Date | undefined]
  >([new Date(), new Date()])

  const [loginId, setLoginId] = useState("")
  const [symbolStr, setSymbolStr] = useState<string>("")
  const [finalState, setFinalState] = useState<{
    year: string
    loginId: string
    symbolStr: string
    startDate: Date | undefined
    endDate: Date | undefined
  }>({
    year: year,
    loginId: loginId,
    symbolStr: symbolStr,
    startDate: startDate,
    endDate: endDate,
  })

  const updateFinalState = () => {
    setFinalState({
      year: year,
      loginId,
      symbolStr,
      startDate,
      endDate,
    })
  }

  const {
    data,
    isLoading,
    isError,
    isRefetching,
    isFetched,
    isFetchedAfterMount,
    refetch,
  } = useQuery(
    [
      "open-positions",
      {
        currentPage,
        finalState,
      },
    ],
    () => getTradesByIPService(),
    {
      refetchInterval: 15000,
    }
  )

  useEffect(() => {
    if (isFetchedAfterMount) {
      setIsForcedUpdate(true)
    }
  }, [currentPage, year])

  useEffect(() => {
    if (!isFetchedAfterMount) {
      if (!isRefetching) {
        if (isSetForcedUpdate) {
          setIsForcedUpdate(false)
        }
      }
    }
  }, [isRefetching])

  const gridRef = useRef<any>() // Optional - for accessing Grid's API
  const [rowData, setRowData] = useState() // Set rowData to Array of Objects, one Object per Row

  //   // login order symbol createdAt type volume orderPrice sl tp livePrice
  // {"id":3,"positionID":12,"deal":1213,"order_id":12,"volume":1222,"symbol":"12212","price":12,"RateMargin":121,"loginId":12121,"sl":1231,"tp":11213,
  // "profit":122312,"marketAsk":12312,"marketBid":12312,"action":12312,"entry":12312,"commission":123123,"time":"12312","reason":"12312312","comment":"123123",
  // "ip_address":"192.167.889.121","created_at":null,"updated_at":null,"deleted_at":null}

  const [columnDefs, setColumnDefs] = useState([
    { headerName: "IP Address", field: "ip_address" },
    { headerName: "Login", field: "loginId" },
    { headerName: "Position ID", field: "positionID" },
    { headerName: "Deal", field: "deal" },
    { headerName: "Symbol", field: "symbol" },
    { headerName: "Action", field: "action" },
    { headerName: "Entry", field: "entry" },
    { headerName: "Volume", field: "volume" },
    { headerName: "Price", field: "price" },
    { headerName: "Rate Margin", field: "RateMargin" },
    { headerName: "Price", field: "price" },
    { headerName: "Price S/L", field: "sl" },
    { headerName: "Price T/P", field: "tp" },
    { headerName: "Profit", field: "profit" },
    { headerName: "Market Ask", field: "marketAsk" },
    { headerName: "Market Bid", field: "marketBid" },
    { headerName: "Commission", field: "commission" },
    { headerName: "Comment", field: "comment" },
  ])

  // DefaultColDef sets props common to all Columns
  const defaultColDef = useMemo(
    () => ({
      // make every column editable
      editable: false,
      selectable: true,
      // make every column use 'text' filter by default
      filter: "agTextColumnFilter",
      // enable floating filters by default
      floatingFilter: true,
      // make columns resizable
      resizable: true,
      // disable cell data types
      cellDataType: false,
      sortable: true,
    }),
    []
  )

  const formattedCSVDATA = () => {
    let _dataArray: any[] = [[...captions]]

    return _dataArray
  }

  const generateReport = async () => {
    setIsGeneratingReport(true)

    try {
      const result = await getTradeHistoryOfAllUsers(
        startDate ? formatDate(startDate) : "",
        endDate ? formatDate(endDate) : startDate ? formatDate(startDate) : ""
      )

      let trades = _.map(result?.data?.data, (_d) => {
        return {
          ..._d?.value,
        }
      })

      let uniqueLogins = _.map(
        _.uniqBy(trades, "login"),
        (_d) => _d?.login
      ).join(",")

      const commissionLogicResult = await fetchCommissionLogicService(
        uniqueLogins,
        formatDate(startDate!).split("-").reverse().join("-")
      )

      console.log(commissionLogicResult)

      const commissionLogics = commissionLogicResult?.data?.data?.length
        ? commissionLogicResult?.data?.data?.reverse()
        : []

      // calculate final
      trades = _.map(trades, (_t) => {
        const cl = _.find(commissionLogics, (_c) =>
          _c?.Login === _t?.login ? true : false
        )

        if (cl) {
          return {
            ..._t,
            final:
              calulateNetValueWithPercent(_t?.mcxNetMTM, cl?.mcx) +
              calulateNetValueWithPercent(_t?.ncdexNetMTM, cl?.ncdx) +
              calulateNetValueWithPercent(_t?.nfoNetMTM, cl?.nfo),
            pati: `In N${cl?.ncdx}, M${cl?.mcx}, F${cl?.nfo}`,
          }
        }

        return {
          ..._t,
          final: _t?.mcxNetMTM + _t?.ncdexNetMTM + _t?.nfoNetMTM,
          pati: `In N100, M100, F100`,
        }
      })

      const name = `FROM ${startDate ? formatDate(startDate) : ""} to ${
        endDate ? formatDate(endDate) : startDate ? formatDate(startDate) : ""
      }`

      downloadCsv(trades, name)
    } catch (error) {}

    setIsGeneratingReport(false)
  }

  const calulateNetValueWithPercent = (_v: number, _percent: number) => {
    try {
      let p = _percent / 100
      return _v * p
    } catch (error) {}
    return _v
  }

  const convertToCsv = (json: any) => {
    const items = json.map((item: any) => {
      return Object.values(item).map((value) => {
        if (typeof value === "string") {
          // Wrap values containing commas in double quotes
          return `"${value}"`
        }
        return value
      })
    })

    const header = Object.keys(json[0]).map((name) => `"${name}"`)
    const csv = [header.join(",")]
      .concat(items.map((row: any) => row.join(",")))
      .join("\n")
    return csv
  }

  const downloadCsv = (jsonData: any, fileName: string) => {
    const csvData = convertToCsv(jsonData)
    const blob = new Blob([csvData], { type: "text/csv" })
    const link = document.createElement("a")
    link.href = window.URL.createObjectURL(blob)
    link.download = `${fileName}.csv`
    link.click()
  }

  return (
    <>
      <Grid
        templateColumns={[
          "repeat(1, 1fr)",
          "repeat(2, 1fr)",
          "repeat(3, 1fr)",
          "repeat(4, 1fr)",
          "repeat(5, 1fr)",
        ]}
        gap={6}
      >
        <Box position="relative">
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            setDateRange={setDateRange}
          />
        </Box>

        <Box>
          <Flex align="center">
            <Box pt={[0, "35px"]} pr={4}>
              <Button
                colorScheme="blue"
                onClick={() => {
                  generateReport()
                }}
                isLoading={isGeneratingReport}
              >
                <Box as={FaDownload} mr={2} />
                Download
              </Button>
            </Box>
          </Flex>
        </Box>

        <Box position="relative"> </Box>

        <Box position="relative"> </Box>

        <Box position="relative">
          <Flex align="center">
            <Box pt={[0, "35px"]} pr={4} width="100%" textAlign="right">
              <Button
                ml="auto"
                colorScheme="blue"
                onClick={() => {
                  onOpen()
                }}
              >
                <Box as={FaPlus} mr={2} />
                Add Sharing Logic
              </Button>
            </Box>
          </Flex>
        </Box>
      </Grid>

      <Box textAlign="right" my={4}>
        {/* <CLink
          href="http://194.163.185.160/backend/api/csv-open-positions"
          isExternal={true}
        >
          <Button colorScheme="green">Download</Button>
        </CLink> */}
      </Box>

      <Box my={4}></Box>

      <IdSharingModal isOpen={isOpen} onClose={onClose} />
    </>
  )
}

export default CommissionReportPage

function getUpcomingMondays() {
  const today = new Date()
  const currentYear = today.getFullYear()
  const upcomingMondays = []

  // Set the date to January 1st of the current year
  today.setMonth(0, 1)

  // Find the first Monday in the year
  while (today.getDay() !== 1) {
    today.setDate(today.getDate() + 1)
  }

  // Add only upcoming Mondays to the array
  while (today.getFullYear() === currentYear) {
    if (today.getTime() > Date.now()) {
      upcomingMondays.push(new Date(today.getTime()))
    }
    today.setDate(today.getDate() + 7)
  }

  return upcomingMondays
}

function formatDateToDDMMYYYY(date: Date) {
  const day = String(date.getDate()).padStart(2, "0")
  const month = String(date.getMonth() + 1).padStart(2, "0") // Months are zero-based
  const year = date.getFullYear()

  return `${day}-${month}-${year}`
}

const IdSharingModal = ({ isOpen, onClose }: any) => {
  const toast = useShowToastHook()

  const formik = useFormik({
    initialValues: {
      Login: undefined,
      created_at: undefined,
      ncdx: 100,
      mcx: 100,
      nfo: 100,
    },
    onSubmit: async (values, actions) => {
      actions.setSubmitting(true)

      try {
        // @ts-ignore
        const path = new URLSearchParams(values).toString()
        const result = await createSharingLogicService(path)

        if (result?.data?.status) {
          formik.resetForm()
          toast.showSuccess("Logic added successfull")
          actions.setSubmitting(false)
          onClose()
          return
        } else {
        }
      } catch (error) {}

      toast.showError("Something went wrong")
      actions.setSubmitting(false)
    },
  })

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      closeOnEsc={false}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add New Logic</ModalHeader>
        {!formik?.isSubmitting && <ModalCloseButton />}
        <ModalBody>
          <form onSubmit={formik.handleSubmit}>
            <Grid
              my={6}
              gap={6}
              width="100%"
              maxWidth={["100%", "100%", "900px", "900px"]}
            >
              <Input
                title="Login"
                type="number"
                name="Login"
                value={formik?.values?.Login}
                onChange={formik?.handleChange}
                onBlur={formik?.handleBlur}
                errorMessage={
                  formik?.touched?.Login && formik?.errors?.Login
                    ? formik?.errors?.Login
                    : ""
                }
              />

              <Input
                title="NCDX"
                type="number"
                name="ncdx"
                value={formik?.values?.ncdx}
                onChange={formik?.handleChange}
                onBlur={formik?.handleBlur}
                errorMessage={
                  formik?.touched?.ncdx && formik?.errors?.ncdx
                    ? formik?.errors?.ncdx
                    : ""
                }
              />

              <Input
                title="mcx"
                type="number"
                name="mcx"
                value={formik?.values?.mcx}
                onChange={formik?.handleChange}
                onBlur={formik?.handleBlur}
                errorMessage={
                  formik?.touched?.mcx && formik?.errors?.mcx
                    ? formik?.errors?.mcx
                    : ""
                }
              />

              <Input
                title="nfo"
                type="number"
                name="nfo"
                value={formik?.values?.nfo}
                onChange={formik?.handleChange}
                onBlur={formik?.handleBlur}
                errorMessage={
                  formik?.touched?.nfo && formik?.errors?.nfo
                    ? formik?.errors?.nfo
                    : ""
                }
              />

              <InputWrapper
                title="Start From"
                errorMessage={
                  formik?.touched?.created_at && formik?.errors?.created_at
                    ? formik?.errors?.created_at
                    : ""
                }
              >
                <Select
                  name="created_at"
                  value={formik?.values?.created_at}
                  onChange={formik?.handleChange}
                  onBlur={formik?.handleBlur}
                >
                  <option value={undefined}>Select Starting Date</option>
                  {_.map(getUpcomingMondays(), (_day: Date) => {
                    return (
                      <option
                        value={formatDateToDDMMYYYY(_day)
                          .split("-")
                          .reverse()
                          .join("-")}
                      >
                        {formatDateToDDMMYYYY(_day)}
                      </option>
                    )
                  })}
                </Select>
              </InputWrapper>

              <Box textAlign="right">
                <Button
                  isLoading={formik?.isSubmitting}
                  colorScheme="blue"
                  mr={3}
                  type="submit"
                >
                  Submit
                </Button>
              </Box>
            </Grid>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
